/** Protocol level types. Nothing in this file depends on React or the DOM. */

export interface NostrEvent {
  id: string
  pubkey: string
  created_at: number
  kind: number
  tags: string[][]
  content: string
  sig: string
}

export type NostrFilter = {
  ids?: string[]
  authors?: string[]
  kinds?: number[]
  since?: number
  until?: number
  limit?: number
  search?: string
} & Record<string, unknown>

/** A draft ready to be signed by a signer. */
export interface DraftEvent {
  kind: number
  content: string
  tags: string[][]
  created_at: number
}

export type RelayState = "idle" | "connecting" | "connected" | "reconnecting" | "closed" | "error"

export interface RelayInfo {
  url: string
  shortUrl: string
  name?: string
  description?: string
  icon?: string
  pubkey?: string
  contact?: string
  supported_nips?: number[]
  software?: string
  version?: string
  tags?: string[]
  payments_url?: string
  limitation?: {
    auth_required?: boolean
    payment_required?: boolean
    restricted_writes?: boolean
  }
}

export interface RelayUsage {
  read: boolean
  write: boolean
}

export interface RelayDescriptor extends RelayUsage {
  url: string
  /** Lower is preferred. */
  priority: number
}

/** A locally stored (or kind 30002 backed) group of relays. */
export interface RelaySet {
  id: string
  /** `["a", "30002:<pubkey>:<id>"]` when the set is published. */
  aTag?: string[]
  name: string
  relayUrls: string[]
  /** Preset sets from community mode cannot be edited or removed. */
  preset?: boolean
}

export type FeedType = "following" | "relays" | "relay"

export interface FeedInfo {
  feedType: FeedType
  /** Relay set id for `relays`, relay URL for `relay`, undefined for `following`. */
  id?: string
}

export interface RelayStatus {
  url: string
  state: RelayState
  read: boolean
  write: boolean
  latency?: number
  eventsReceived: number
  lastError?: string
  lastConnectedAt?: number
}

export interface PublishResult {
  relay: string
  ok: boolean
  message: string
}

export interface PublishSummary {
  event: NostrEvent
  results: PublishResult[]
  accepted: number
  rejected: number
  timedOut: number
}

export interface Profile {
  pubkey: string
  npub: string
  name?: string
  displayName?: string
  about?: string
  picture?: string
  banner?: string
  nip05?: string
  lud16?: string
  lud06?: string
  website?: string
  createdAt?: number
}

export const KINDS = {
  PROFILE: 0,
  NOTE: 1,
  CONTACT_LIST: 3,
  DELETE: 5,
  REPOST: 6,
  REACTION: 7,
  MUTE_LIST: 10000,
  RELAY_LIST: 10002,
  BOOKMARK_LIST: 10003,
  FAVORITE_RELAYS: 10012,
  ZAP_REQUEST: 9734,
  ZAP_RECEIPT: 9735,
  DM: 4,
  GIFT_WRAP: 1059,
  ARTICLE: 30023,
  RELAY_SETS: 30002,
  RELAY_REVIEW: 31987,
  RELAY_DISCOVERY: 30166,
  FOLLOW_PACK: 39089,
} as const

export function isReplaceable(kind: number): boolean {
  return kind === 0 || kind === 3 || (kind >= 10000 && kind < 20000)
}

export function isAddressable(kind: number): boolean {
  return kind >= 30000 && kind < 40000
}

export function isEphemeral(kind: number): boolean {
  return kind >= 20000 && kind < 30000
}

export function isRelaySetKind(kind: number): boolean {
  return kind === KINDS.RELAY_SETS
}

export function eventAddress(event: NostrEvent): string {
  if (isAddressable(event.kind)) {
    const identifier = event.tags.find((tag) => tag[0] === "d")?.[1] ?? ""
    return `${event.kind}:${event.pubkey}:${identifier}`
  }
  return `${event.kind}:${event.pubkey}`
}

export function tagValue(event: NostrEvent, name: string): string | undefined {
  return event.tags.find((tag) => tag[0] === name)?.[1]
}

export function tagValues(event: NostrEvent, name: string): string[] {
  return event.tags.filter((tag) => tag[0] === name).map((tag) => tag[1]).filter(Boolean)
}
